import { eventChannel, type EventChannel } from 'redux-saga';
import { call, cancelled, put, select, take, takeEvery, takeLatest } from 'redux-saga/effects';
import type { Profile, ProfileComponent } from '@nx/domain';
import {
  normalizeSlug,
  profileRoleFromPartnerRole,
  profileRoleTypesEqual,
  sanitizeProfileComponentConfig,
} from '@nx/domain';
import { claimProfileSlug } from '../../services/profileCallables';
import { mediaRepository } from '../../services/eoRepositories';
import {
  getProfileOwnerId,
  profileRepository,
} from '../../services/profileRepositories';
import { extractImageEdgeColor } from '../../utils/extractImageEdgeColor';
import { safeCssColor } from '../../utils/safeCssColor';
import { validateProfileImageFile } from '../../utils/validateProfileImageFile';
import { pushToast } from '../slices/eoUiSlice';
import type { ProfileMediaUploadKind } from '../slices/profileSlice';
import type { RootState } from '../store';
import {
  componentAddRequested,
  componentMutationFinished,
  componentRemoveRequested,
  componentReorderRequested,
  componentConfigPatched,
  componentConfigPersistRequested,
  componentUpdateRequested,
  componentsLoaded,
  profileComponentsWatchRequested,
  profileAvatarUploadRequested,
  profileBackgroundRemoveRequested,
  profileBackgroundUploadRequested,
  profileComponentImageRemoveRequested,
  profileComponentImageUploadRequested,
  profileCreateRequested,
  profileFailed,
  profileLoadRequested,
  profileLoaded,
  profileMediaUploadFinished,
  profilePublishRequested,
  profileSaveRequested,
  profileSaveSucceeded,
  profileUnpublishRequested,
} from '../slices/profileSlice';

function createComponentsChannel(profileId: string): EventChannel<ProfileComponent[]> {
  return eventChannel<ProfileComponent[]>((emit) =>
    profileRepository.watchComponents(profileId, (components) => emit(components)),
  );
}

function* watchComponents(profileId: string): Generator<unknown, void, unknown> {
  const channel = (yield call(createComponentsChannel, profileId)) as ReturnType<
    typeof createComponentsChannel
  >;
  try {
    while (true) {
      const components = (yield take(channel)) as ProfileComponent[];
      yield put(componentsLoaded(components));
    }
  } finally {
    channel.close();
  }
}

export function* handleComponentsWatch(
  action: ReturnType<typeof profileComponentsWatchRequested>,
): Generator<unknown, void, unknown> {
  if (!action.payload) return;
  yield call(watchComponents, action.payload);
}

function* selectDerivedProfileRoleType(): Generator<unknown, Profile['roleType'], unknown> {
  const partnerRole = (yield select((s: RootState) => s.auth.identity?.partnerRole)) as
    | Profile['roleType']
    | null
    | undefined;
  return profileRoleFromPartnerRole(partnerRole);
}

function* reconcileProfileRoleType(
  profile: Profile | null,
): Generator<unknown, Profile | null, unknown> {
  if (!profile) return null;
  const expectedRoleType = (yield call(selectDerivedProfileRoleType)) as Profile['roleType'];
  if (profileRoleTypesEqual(profile.roleType, expectedRoleType)) {
    return profile;
  }
  return (yield call(
    [profileRepository, profileRepository.update],
    profile.id,
    { roleType: expectedRoleType },
  )) as Profile;
}

function* persistOwnerProfileDraft(profile: Profile): Generator<unknown, Profile, unknown> {
  const ownerId = getProfileOwnerId();
  if (!ownerId) throw new Error('Not signed in');

  const roleType = (yield call(selectDerivedProfileRoleType)) as Profile['roleType'];

  const current = (yield call(
    [profileRepository, profileRepository.getByOwner],
    ownerId,
  )) as Profile | null;
  if (!current) throw new Error('Profile not found');

  const slug = normalizeSlug(profile.slug);
  if (!slug) throw new Error('Slug required');

  yield call(claimProfileSlug, current.id, slug);

  const updated = (yield call(
    [profileRepository, profileRepository.update],
    current.id,
    {
      title: profile.title,
      description: profile.description,
      tagline: profile.tagline,
      location: profile.location,
      roleType,
      tags: profile.tags,
      themeConfig: profile.themeConfig,
      avatarUrl: profile.avatarUrl,
    },
  )) as Profile;

  const reloaded = (yield call(
    [profileRepository, profileRepository.getById],
    current.id,
  )) as Profile | null;
  return reloaded ?? updated;
}

export function* handleProfileLoad(): Generator<unknown, void, unknown> {
  try {
    const ownerId = getProfileOwnerId();
    if (!ownerId) {
      yield put(profileLoaded(null));
      yield put(profileComponentsWatchRequested(null));
      return;
    }
    const profile = (yield call([profileRepository, profileRepository.getByOwner], ownerId)) as Profile | null;
    const reconciled = (yield call(reconcileProfileRoleType, profile)) as Profile | null;
    yield put(profileLoaded(reconciled));
    if (reconciled) {
      yield put(profileComponentsWatchRequested(reconciled.id));
    } else {
      yield put(profileComponentsWatchRequested(null));
    }
  } catch (e) {
    yield put(profileFailed(e instanceof Error ? e.message : 'Failed to load profile'));
  }
}

export function* handleProfileCreate(
  action: ReturnType<typeof profileCreateRequested>,
): Generator<unknown, void, unknown> {
  let createdId: string | null = null;
  try {
    const ownerId = getProfileOwnerId();
    if (!ownerId) throw new Error('Not signed in');

    const existing = (yield call(
      [profileRepository, profileRepository.getByOwner],
      ownerId,
    )) as Profile | null;
    if (existing) {
      yield put(profileSaveSucceeded(existing));
      yield put(profileComponentsWatchRequested(existing.id));
      return;
    }

    const slug = normalizeSlug(action.payload.slug ?? 'my-profile');
    const roleType = (yield call(selectDerivedProfileRoleType)) as Profile['roleType'];
    const profile = (yield call(
      [profileRepository, profileRepository.create],
      ownerId,
      { ...action.payload, slug: '', roleType },
    )) as Profile;
    createdId = profile.id;

    yield call(claimProfileSlug, profile.id, slug);
    const updated = (yield call(
      [profileRepository, profileRepository.getById],
      profile.id,
    )) as Profile | null;
    if (!updated) throw new Error('Failed to load created profile');

    yield put(profileSaveSucceeded(updated));
    yield put(profileComponentsWatchRequested(updated.id));
    yield put(pushToast({ msg: 'profile.toast.created', kind: 'success' }));
  } catch (e) {
    if (createdId) {
      try {
        yield call([profileRepository, profileRepository.delete], createdId);
      } catch {
        // best-effort cleanup
      }
    }
    yield put(profileFailed(e instanceof Error ? e.message : 'Failed to create profile'));
    yield put(pushToast({ msg: 'profile.toast.createFailed', kind: 'danger' }));
  }
}

export function* handleProfileSave(
  action: ReturnType<typeof profileSaveRequested>,
): Generator<unknown, void, unknown> {
  try {
    const ownerId = getProfileOwnerId();
    if (!ownerId) throw new Error('Not signed in');
    const current = (yield call([profileRepository, profileRepository.getByOwner], ownerId)) as Profile | null;
    if (!current) throw new Error('Profile not found');

    const draft: Profile = {
      ...current,
      ...action.payload,
      themeConfig: action.payload.themeConfig
        ? { ...current.themeConfig, ...action.payload.themeConfig }
        : current.themeConfig,
    };

    const profile = (yield call(persistOwnerProfileDraft, draft)) as Profile;
    yield put(profileSaveSucceeded(profile));
    yield put(pushToast({ msg: 'profile.toast.draftSaved', kind: 'success' }));
  } catch (e) {
    yield put(profileFailed(e instanceof Error ? e.message : 'Failed to save profile'));
    yield put(pushToast({ msg: 'profile.toast.saveFailed', kind: 'danger' }));
  }
}

export function* handleProfilePublish(): Generator<unknown, void, unknown> {
  try {
    const draft = (yield select((s: RootState) => s.profile.profile)) as Profile | null;
    if (!draft) throw new Error('Profile not found');

    const saved = (yield call(persistOwnerProfileDraft, draft)) as Profile;
    if (!saved.slug) throw new Error('Slug required to publish');

    const published = (yield call(
      [profileRepository, profileRepository.update],
      saved.id,
      { status: 'published' },
    )) as Profile;
    yield put(profileSaveSucceeded(published));
    yield put(pushToast({ msg: 'profile.toast.published', kind: 'success' }));
  } catch (e) {
    yield put(profileFailed(e instanceof Error ? e.message : 'Failed to publish'));
    yield put(pushToast({ msg: 'profile.toast.publishFailed', kind: 'danger' }));
  }
}

function* handleProfileUnpublish(): Generator<unknown, void, unknown> {
  try {
    const ownerId = getProfileOwnerId();
    if (!ownerId) throw new Error('Not signed in');
    const profile = (yield call([profileRepository, profileRepository.getByOwner], ownerId)) as Profile | null;
    if (!profile) throw new Error('Profile not found');
    const updated = (yield call(
      [profileRepository, profileRepository.update],
      profile.id,
      { status: 'draft' },
    )) as Profile;
    yield put(profileSaveSucceeded(updated));
    yield put(pushToast({ msg: 'profile.toast.unpublished', kind: 'danger' }));
  } catch (e) {
    yield put(profileFailed(e instanceof Error ? e.message : 'Failed to unpublish'));
    yield put(pushToast({ msg: 'profile.toast.unpublishFailed', kind: 'danger' }));
  }
}

function* handleComponentAdd(
  action: ReturnType<typeof componentAddRequested>,
): Generator<unknown, void, unknown> {
  try {
    const profile = (yield call(getOwnerProfile)) as Profile;
    yield call(
      [profileRepository, profileRepository.addComponent],
      profile.id,
      action.payload,
    );
    yield put(componentMutationFinished());
    yield put(pushToast({ msg: 'profile.toast.blockAdded', kind: 'success' }));
  } catch (e) {
    yield put(profileFailed(e instanceof Error ? e.message : 'Failed to add block'));
    yield put(pushToast({ msg: 'profile.toast.blockAddFailed', kind: 'danger' }));
  }
}

function* handleComponentUpdate(
  action: ReturnType<typeof componentUpdateRequested>,
): Generator<unknown, void, unknown> {
  try {
    const profile = (yield call(getOwnerProfile)) as Profile;
    yield call(
      [profileRepository, profileRepository.updateComponent],
      profile.id,
      action.payload.componentId,
      action.payload.input,
    );
    yield put(componentMutationFinished());
  } catch (e) {
    yield put(profileFailed(e instanceof Error ? e.message : 'Failed to update block'));
  }
}

function* isComponentMediaBusy(componentId: string): Generator<unknown, boolean, unknown> {
  const mediaUploading = (yield select((s: RootState) => s.profile.mediaUploading)) as
    | ProfileMediaUploadKind
    | null;
  const mediaUploadingComponentId = (yield select(
    (s: RootState) => s.profile.mediaUploadingComponentId,
  )) as string | null;
  return mediaUploading === 'component-image' && mediaUploadingComponentId === componentId;
}

export function* handleComponentConfigPersist(
  action: ReturnType<typeof componentConfigPersistRequested>,
): Generator<unknown, void, unknown> {
  try {
    const componentId = action.payload;
    if ((yield call(isComponentMediaBusy, componentId)) as boolean) {
      return;
    }

    const components = (yield select((s: RootState) => s.profile.components)) as ProfileComponent[];
    const local = components.find((c) => c.id === componentId);
    if (!local) return;

    const profile = (yield call(getOwnerProfile)) as Profile;
    yield call(
      [profileRepository, profileRepository.updateComponent],
      profile.id,
      componentId,
      {
        config: sanitizeProfileComponentConfig(local.config),
        replaceConfig: true,
      },
    );
    yield put(componentMutationFinished());
  } catch (e) {
    yield put(profileFailed(e instanceof Error ? e.message : 'Failed to update block'));
  }
}

function* handleComponentRemove(
  action: ReturnType<typeof componentRemoveRequested>,
): Generator<unknown, void, unknown> {
  try {
    const profile = (yield call(getOwnerProfile)) as Profile;
    yield call([profileRepository, profileRepository.removeComponent], profile.id, action.payload);
    yield put(componentMutationFinished());
  } catch (e) {
    yield put(profileFailed(e instanceof Error ? e.message : 'Failed to remove block'));
  }
}

function* handleComponentReorder(
  action: ReturnType<typeof componentReorderRequested>,
): Generator<unknown, void, unknown> {
  try {
    const profile = (yield call(getOwnerProfile)) as Profile;
    yield call(
      [profileRepository, profileRepository.reorderComponents],
      profile.id,
      action.payload,
    );
    yield put(componentMutationFinished());
  } catch (e) {
    yield put(profileFailed(e instanceof Error ? e.message : 'Failed to reorder blocks'));
  }
}

function* getOwnerProfile(): Generator<unknown, Profile, unknown> {
  const ownerId = getProfileOwnerId();
  if (!ownerId) throw new Error('Not signed in');
  const profile = (yield call([profileRepository, profileRepository.getByOwner], ownerId)) as Profile | null;
  if (!profile) throw new Error('Profile not found');
  return profile;
}

function* deleteMediaBestEffort(url: string | null | undefined): Generator<unknown, void, unknown> {
  if (!url) return;
  try {
    yield call([mediaRepository, mediaRepository.deleteByUrl], url);
  } catch {
    // best-effort cleanup
  }
}

function buildDraftProfile(
  current: Profile,
  patch: {
    avatarUrl?: string | null;
    themeConfig?: Partial<Profile['themeConfig']>;
  },
): Profile {
  return {
    ...current,
    ...(patch.avatarUrl !== undefined ? { avatarUrl: patch.avatarUrl } : {}),
    themeConfig: patch.themeConfig
      ? { ...current.themeConfig, ...patch.themeConfig }
      : current.themeConfig,
  };
}

export function* handleProfileAvatarUpload(
  action: ReturnType<typeof profileAvatarUploadRequested>,
): Generator<unknown, void, unknown> {
  let uploadedUrl: string | null = null;
  try {
    const ownerId = getProfileOwnerId();
    if (!ownerId) throw new Error('Not signed in');

    const validation = (yield call(validateProfileImageFile, action.payload.file)) as
      | string
      | Awaited<ReturnType<typeof validateProfileImageFile>>;
    if (typeof validation === 'string') {
      yield put(pushToast({ msg: validation, kind: 'danger' }));
      return;
    }

    const profile = (yield select((s: RootState) => s.profile.profile)) as Profile | null;
    if (!profile) throw new Error('Profile not found');

    uploadedUrl = (yield call(
      [mediaRepository, mediaRepository.uploadProfileAvatar],
      ownerId,
      validation.file,
      validation.fileName,
    )) as string;

    const freshProfile = (yield select((s: RootState) => s.profile.profile)) as Profile | null;
    if (!freshProfile) throw new Error('Profile not found');
    const saved = (yield call(
      persistOwnerProfileDraft,
      buildDraftProfile(freshProfile, { avatarUrl: uploadedUrl }),
    )) as Profile;
    yield put(profileSaveSucceeded(saved));
    yield put(pushToast({ msg: 'profile.toast.avatarUploaded', kind: 'success' }));
  } catch (e) {
    yield call(deleteMediaBestEffort, uploadedUrl);
    yield put(profileFailed(e instanceof Error ? e.message : 'Failed to upload avatar'));
    yield put(pushToast({ msg: 'profile.toast.avatarUploadFailed', kind: 'danger' }));
  } finally {
    yield put(profileMediaUploadFinished());
  }
}

export function* handleProfileBackgroundUpload(
  action: ReturnType<typeof profileBackgroundUploadRequested>,
): Generator<unknown, void, unknown> {
  let uploadedUrl: string | null = null;
  try {
    const ownerId = getProfileOwnerId();
    if (!ownerId) throw new Error('Not signed in');

    const validation = (yield call(validateProfileImageFile, action.payload.file)) as
      | string
      | Awaited<ReturnType<typeof validateProfileImageFile>>;
    if (typeof validation === 'string') {
      yield put(pushToast({ msg: validation, kind: 'danger' }));
      return;
    }

    const profile = (yield select((s: RootState) => s.profile.profile)) as Profile | null;
    if (!profile) throw new Error('Profile not found');

    const extractedColor = (yield call(extractImageEdgeColor, validation.file)) as string | null;
    const backgroundColor = safeCssColor(extractedColor);

    uploadedUrl = (yield call(
      [mediaRepository, mediaRepository.uploadProfileBackground],
      ownerId,
      profile.id,
      validation.file,
      validation.fileName,
    )) as string;

    const freshProfile = (yield select((s: RootState) => s.profile.profile)) as Profile | null;
    if (!freshProfile) throw new Error('Profile not found');
    const saved = (yield call(
      persistOwnerProfileDraft,
      buildDraftProfile(freshProfile, {
        themeConfig: { backgroundImageUrl: uploadedUrl, backgroundColor },
      }),
    )) as Profile;
    yield put(profileSaveSucceeded(saved));
    yield put(pushToast({ msg: 'profile.toast.backgroundUploaded', kind: 'success' }));
  } catch (e) {
    yield call(deleteMediaBestEffort, uploadedUrl);
    yield put(profileFailed(e instanceof Error ? e.message : 'Failed to upload background'));
    yield put(pushToast({ msg: 'profile.toast.backgroundUploadFailed', kind: 'danger' }));
  } finally {
    yield put(profileMediaUploadFinished());
  }
}

export function* handleProfileBackgroundRemove(): Generator<unknown, void, unknown> {
  let previousUrl: string | null = null;
  try {
    const profile = (yield select((s: RootState) => s.profile.profile)) as Profile | null;
    if (!profile) throw new Error('Profile not found');

    previousUrl = profile.themeConfig.backgroundImageUrl;
    if (!previousUrl) return;

    const saved = (yield call(
      persistOwnerProfileDraft,
      buildDraftProfile(profile, { themeConfig: { backgroundImageUrl: null, backgroundColor: null } }),
    )) as Profile;
    yield put(profileSaveSucceeded(saved));
    yield call(deleteMediaBestEffort, previousUrl);
    yield put(pushToast({ msg: 'profile.toast.backgroundRemoved', kind: 'success' }));
  } catch (e) {
    yield put(profileFailed(e instanceof Error ? e.message : 'Failed to remove background'));
    yield put(pushToast({ msg: 'profile.toast.backgroundRemoveFailed', kind: 'danger' }));
  } finally {
    yield put(profileMediaUploadFinished());
  }
}

export function* handleProfileComponentImageRemove(
  action: ReturnType<typeof profileComponentImageRemoveRequested>,
): Generator<unknown, void, unknown> {
  const { componentId } = action.payload;
  let previousUrl: string | null = null;
  try {
    const components = (yield select((s: RootState) => s.profile.components)) as ProfileComponent[];
    const local = components.find((c) => c.id === componentId);
    if (!local) throw new Error('Component not found');

    previousUrl = local.config.imageUrl ?? null;
    if (!previousUrl) return;

    const profile = (yield call(getOwnerProfile)) as Profile;
    const nextConfig = sanitizeProfileComponentConfig({ ...local.config, imageUrl: '' });
    yield put(componentConfigPatched({ componentId, config: { imageUrl: '' } }));
    yield call(
      [profileRepository, profileRepository.updateComponent],
      profile.id,
      componentId,
      { config: nextConfig, replaceConfig: true },
    );
    yield put(componentMutationFinished());
    yield call(deleteMediaBestEffort, previousUrl);
    yield put(pushToast({ msg: 'profile.toast.blockImageRemoved', kind: 'success' }));
  } catch (e) {
    if (yield cancelled()) return;
    yield put(profileFailed(e instanceof Error ? e.message : 'Failed to remove block image'));
    yield put(pushToast({ msg: 'profile.toast.blockImageRemoveFailed', kind: 'danger' }));
  } finally {
    if (yield cancelled()) return;
    yield put(profileMediaUploadFinished());
  }
}

export function* handleProfileComponentImageUpload(
  action: ReturnType<typeof profileComponentImageUploadRequested>,
): Generator<unknown, void, unknown> {
  let uploadedUrl: string | null = null;
  const { componentId, file } = action.payload;
  try {
    const ownerId = getProfileOwnerId();
    if (!ownerId) throw new Error('Not signed in');

    const components = (yield select((s: RootState) => s.profile.components)) as ProfileComponent[];
    const local = components.find((c) => c.id === componentId);
    if (!local) throw new Error('Component not found');

    const validation = (yield call(validateProfileImageFile, file)) as
      | string
      | Awaited<ReturnType<typeof validateProfileImageFile>>;
    if (typeof validation === 'string') {
      yield put(pushToast({ msg: validation, kind: 'danger' }));
      return;
    }

    const profile = (yield select((s: RootState) => s.profile.profile)) as Profile | null;
    if (!profile) throw new Error('Profile not found');

    uploadedUrl = (yield call(
      [mediaRepository, mediaRepository.uploadProfileComponentImage],
      ownerId,
      profile.id,
      componentId,
      validation.file,
      validation.fileName,
    )) as string;

    const latestComponents = (yield select((s: RootState) => s.profile.components)) as ProfileComponent[];
    const latestLocal = latestComponents.find((c) => c.id === componentId);
    if (!latestLocal) throw new Error('Component not found');

    yield put(componentConfigPatched({ componentId, config: { imageUrl: uploadedUrl } }));
    yield call(
      [profileRepository, profileRepository.updateComponent],
      profile.id,
      componentId,
      {
        config: sanitizeProfileComponentConfig({ ...latestLocal.config, imageUrl: uploadedUrl }),
        replaceConfig: true,
      },
    );
    yield put(componentMutationFinished());
    yield put(pushToast({ msg: 'profile.toast.blockImageUploaded', kind: 'success' }));
  } catch (e) {
    if (yield cancelled()) return;
    yield call(deleteMediaBestEffort, uploadedUrl);
    yield put(profileFailed(e instanceof Error ? e.message : 'Failed to upload block image'));
    yield put(pushToast({ msg: 'profile.toast.blockImageUploadFailed', kind: 'danger' }));
  } finally {
    if (yield cancelled()) return;
    yield put(profileMediaUploadFinished());
  }
}

export function* profileSaga() {
  yield takeLatest(profileComponentsWatchRequested.type, handleComponentsWatch);
  yield takeLatest(profileLoadRequested.type, handleProfileLoad);
  yield takeLatest(profileCreateRequested.type, handleProfileCreate);
  yield takeLatest(profileSaveRequested.type, handleProfileSave);
  yield takeLatest(profilePublishRequested.type, handleProfilePublish);
  yield takeLatest(profileUnpublishRequested.type, handleProfileUnpublish);
  yield takeLatest(componentAddRequested.type, handleComponentAdd);
  yield takeEvery(componentUpdateRequested.type, handleComponentUpdate);
  yield takeEvery(componentConfigPersistRequested.type, handleComponentConfigPersist);
  yield takeLatest(componentRemoveRequested.type, handleComponentRemove);
  yield takeLatest(componentReorderRequested.type, handleComponentReorder);
  yield takeLatest(profileAvatarUploadRequested.type, handleProfileAvatarUpload);
  yield takeLatest(profileBackgroundUploadRequested.type, handleProfileBackgroundUpload);
  yield takeLatest(profileBackgroundRemoveRequested.type, handleProfileBackgroundRemove);
  yield takeLatest(profileComponentImageUploadRequested.type, handleProfileComponentImageUpload);
  yield takeLatest(profileComponentImageRemoveRequested.type, handleProfileComponentImageRemove);
}
